// Cart and product photo fix for Shopify CDN images (mainly the AOV cart drawer, which renders on its own).
// Some product photos in the store are .heic files. Shopify's CDN has to convert those on the fly, the raw file
// can't show in most browsers, and a first (cold) conversion often comes back as a 502, so the photo shows broken.
// 1) Product photos (Shopify CDN /files/ or /products/) with no width= get one, so the CDN serves a resized
//    JPEG/WebP/AVIF: every one inside the AOV drawer (#aov-cart-drawer), and any .heic one elsewhere on the page.
//    Theme assets (/t/<n>/assets/) and SVGs are left alone.
// 2) If a product photo fails to load, retry it a few times (the CDN usually has it ready on the next request).
// The real fix is re-uploading those photos as JPG or WebP in Shopify admin.
// 3) In the AOV drawer, products we've shot show our own packshot instead of the store photo (see swap below).
(() => {
  const CDN = /(^|\/\/)(cdn\.shopify\.com\/|[^/]+\/cdn\/shop\/)/;
  const DRAWER = '#aov-cart-drawer';
  const isCdn = u => !!u && CDN.test(u) && /\/(files|products)\//.test(u) && !/\/t\/\d+\/assets\//.test(u) && !/\.svg(\?|$)/i.test(u);
  const isHeic = u => /\.heic(\?|$)/i.test(u);
  const hasWidth = u => /[?&]width=\d/.test(u);
  const sizeFor = (img, inDrawer) => {
    const w = Math.max(img.clientWidth || 0, +img.getAttribute('width') || 0);
    if (!w) return inDrawer ? 300 : 800;
    const px = Math.ceil(w * Math.min(window.devicePixelRatio || 1, 2) / 100) * 100;
    return Math.min(Math.max(px, 300), 2000);
  };
  const addWidth = (u, w) => u + (u.includes('?') ? '&' : '?') + 'width=' + w;

  // returns the new srcset, or null when nothing needed a width (so we never write, and never loop)
  function fixSrcset(v, w, ok) {
    let changed = false;
    const out = v.split(',').map(part => {
      const p = part.trim(), bits = p.split(/\s+/), u = bits[0];
      if (!u || !ok(u) || hasWidth(u)) return p;
      changed = true;
      const d = bits[1] && /^\d+w$/.test(bits[1]) ? parseInt(bits[1], 10) : w;
      return [addWidth(u, d)].concat(bits.slice(1)).join(' ');
    }).join(', ');
    return changed ? out : null;
  }

  // at most a few writes per element, in case another script keeps resetting it
  const writes = new WeakMap();
  const write = (el, k, v) => { const n = writes.get(el) || 0; if (n >= 4) return; writes.set(el, n + 1); el.setAttribute(k, v); };

  // 3) Our own packshots (CC.photos, by handle) replace the store photo in the AOV drawer: cart lines through the
  //    variant id in their class (looked up in /cart.js), upsells through their title (one search lookup per title).
  const PHOTOS = (window.CC && window.CC.photos) || {};
  const ROOT = ((window.CC && window.CC.routes && window.CC.routes.root) || '/').replace(/\/$/, '');
  const byVariant = new Map(), byTitle = new Map(), askedCart = new Set();
  let cartReq = null;
  const loadCart = () => cartReq || (cartReq = fetch(ROOT + '/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    .then(r => r.json()).then(c => { (c.items || []).forEach(i => byVariant.set(String(i.variant_id), i.handle)); })
    .catch(() => {}).then(() => { cartReq = null; }));
  const findTitle = t => {
    if (byTitle.has(t)) return byTitle.get(t);
    const p = fetch(ROOT + '/search/suggest.json?q=' + encodeURIComponent(t) + '&resources[type]=product&resources[limit]=6', { headers: { Accept: 'application/json' } })
      .then(r => r.json()).then(j => { const hit = ((j.resources && j.resources.results && j.resources.results.products) || []).find(x => x.title === t); return hit ? hit.handle : null; })
      .catch(() => null);
    byTitle.set(t, p);
    return p;
  };
  const usePhoto = (img, handle) => {
    const url = handle && PHOTOS[handle];
    if (!url || img.getAttribute('src') === url) return;
    img.removeAttribute('srcset');
    img.setAttribute('src', url);
  };
  function swap(img) {
    const line = img.closest('[class*="AOV-CDMinimal__Item--"]');
    if (line) {
      const m = line.className.match(/AOV-CDMinimal__Item--(\d+)/);
      if (!m) return;
      const id = m[1];
      if (byVariant.has(id)) return usePhoto(img, byVariant.get(id));
      if (askedCart.has(id)) return;
      askedCart.add(id);
      loadCart().then(() => usePhoto(img, byVariant.get(id)));
      return;
    }
    if (img.closest('.AOV-MinimalUpsellProducts__Item') && img.alt) findTitle(img.alt.trim()).then(h => usePhoto(img, h));
  }

  function fix(el) {
    const inDrawer = !!el.closest(DRAWER);
    if (inDrawer && el.tagName === 'IMG') swap(el);
    const ok = u => isCdn(u) && (inDrawer || isHeic(u));
    const w = el.tagName === 'IMG' ? sizeFor(el, inDrawer) : (inDrawer ? 300 : 800);
    const src = el.getAttribute('src'), set = el.getAttribute('srcset');
    if (el.tagName === 'IMG' && ok(src) && !hasWidth(src)) write(el, 'src', addWidth(src, w));
    if (set && CDN.test(set)) { const n = fixSrcset(set, w, ok); if (n) write(el, 'srcset', n); }
  }
  const scan = root => {
    if (!root || root.nodeType !== 1) return;
    if (root.matches('img,source')) fix(root);
    root.querySelectorAll('img[src*="cdn"],img[srcset*="cdn"],source[srcset*="cdn"]').forEach(fix);
  };

  // retry failed CDN images: same URL again after a short wait, then a nearby width (a fresh conversion)
  const tries = new WeakMap();
  document.addEventListener('error', e => {
    const img = e.target;
    if (!img || img.tagName !== 'IMG') return;
    const u = img.currentSrc || img.src;
    if (!isCdn(u)) return;
    const n = (tries.get(img) || 0) + 1;
    if (n > 4) return;
    tries.set(img, n);
    setTimeout(() => {
      let next = u;
      if (n > 2) next = hasWidth(u) ? u.replace(/([?&]width=)(\d+)/, (m, k, d) => k + (+d + n)) : addWidth(u, 300 + n);
      const probe = new Image();
      probe.onload = () => { if (img.getAttribute('srcset')) img.removeAttribute('srcset'); img.src = next; };
      probe.onerror = () => { img.dispatchEvent(new Event('error')); };
      probe.src = next;
    }, 400 * n);
  }, true);

  const start = () => {
    scan(document.body);
    new MutationObserver(list => {
      for (const m of list) {
        if (m.type === 'attributes') { if (m.target.matches && m.target.matches('img,source')) fix(m.target); }
        else m.addedNodes.forEach(scan);
      }
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
  };
  if (document.body) start(); else document.addEventListener('DOMContentLoaded', start);
})();
